// Pilot sign-in configuration (#323). Auth exists only when a Supabase
// project is configured at BUILD time — the deployed app without these env
// vars carries no Account section, no client bundle chunk and no behaviour
// change, which is what makes the feature safe to merge before the backend
// exists. Values come from .env (see .env.example and docs/pilot-setup.md);
// the anon key is public by design (RLS is the security boundary).

export interface AuthConfig {
  url: string;
  anonKey: string;
}

// Blank counts as unset — .env.example ships both key lines with one left
// empty, and `??` alone would let a blank ANON_KEY shadow a filled
// PUBLISHABLE_KEY (empty string is not nullish).
function pick(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

export function authConfig(): AuthConfig | null {
  const url = pick(import.meta.env.VITE_SUPABASE_URL);
  // Supabase's dashboard now issues "publishable" keys (sb_publishable_…)
  // where it used to show the legacy anon JWT; supabase-js accepts either.
  // Accept both names so copying whichever the dashboard displays just works.
  const anonKey =
    pick(import.meta.env.VITE_SUPABASE_ANON_KEY) ??
    pick(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
  if (!url || !anonKey) return null;
  return { url, anonKey };
}

/** The Google OAuth client id for Google's own sign-in button, so the
 * consent screen names this site rather than the Supabase project's domain.
 * Unset, Google sign-in stays the redirect through Supabase. */
export function googleClientId(): string | null {
  return pick(import.meta.env.VITE_GOOGLE_CLIENT_ID);
}

/** The Apple Services ID for Sign in with Apple. Unset, there is no Apple
 * button. */
export function appleClientId(): string | null {
  return pick(import.meta.env.VITE_APPLE_CLIENT_ID);
}
